<script setup lang="ts">
import type { IconName } from '@/ui-kit/components/icon';

interface Props {
  label?: string;
  disable?: boolean;
  loading?: boolean;
  flat?: boolean;
  outline?: boolean;
  dense?: boolean;
  noHover?: boolean;
  icon?: IconName;
  iconAfter?: boolean;
  submit?: boolean;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'accent' | 'neutral' | 'white';
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  disable: false,
  loading: false,
  flat: false,
  outline: false,
  dense: false,
  noHover: false,
  iconAfter: false,
  icon: undefined,
  submit: false,
  color: 'primary',
});

const emit = defineEmits<{ (event: 'click', value: MouseEvent): void }>();

const handleClick = (event: MouseEvent) => {
  if (props.disable || props.loading) return;
  emit('click', event);
};
</script>

<template>
  <button
    class="ui-button"
    :class="{
      'ui-button--flat': flat,
      'ui-button--outline': outline,
      'ui-button--dense': dense,
      'ui-button--loading': loading,
      'ui-button--no-hover': noHover,
      [`ui-button--color-${color}`]: color,
    }"
    :disabled="disable"
    :type="submit ? 'submit' : 'button'"
    @click="handleClick"
  >
    <span v-if="loading" class="ui-button__spinner" aria-hidden="true" />
    <span class="ui-button__content">
      <span v-if="!iconAfter && icon" class="ui-button__icon">
        <ui-icon :name="icon" />
      </span>
      <span v-if="label || $slots.default" class="ui-button__label">
        <slot>{{ label }}</slot>
      </span>
      <span v-if="iconAfter && icon" class="ui-button__icon">
        <ui-icon :name="icon" />
      </span>
    </span>
  </button>
</template>

<style scoped lang="scss">
.ui-button {
  --ui-button-color: #{$primary};
  --ui-button-text-color: #{$white};
  --ui-button-plain-color: var(--ui-button-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: inherit;
  max-width: 100%;
  padding: 10px 20px;
  border-radius: 12px;
  border: none;
  background: var(--ui-button-color);
  color: var(--ui-button-text-color);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  max-height: 56px;
  position: relative;
  transition:
    opacity 0.2s ease,
    background-color 0.2s ease,
    color 0.2s ease,
    transform 0.12s ease;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  @media (hover: hover) and (pointer: fine) {
    &:not(:disabled):hover {
      transform: scale(1.04);
    }
  }

  &:not(:disabled):active {
    transform: scale(1);
  }

  &--no-hover {
    transform: none !important;
  }

  &--no-hover:hover,
  &--no-hover:active {
    transform: none !important;
  }

  &--flat {
    background: transparent;
    color: var(--ui-button-plain-color);
  }

  &--outline {
    background: transparent;
    border: 1px solid var(--ui-button-color);
    color: var(--ui-button-plain-color);
  }

  &--dense {
    padding: 6px 12px;
    border-radius: 10px;
    font-size: 0.8125rem;
  }

  &__content {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
  }

  &__label {
    white-space: nowrap;
    color: inherit;
  }

  &__spinner {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid rgb(255 255 255 / 40%);
    border-top-color: $white;
    animation: ui-button-spin 0.8s linear infinite;
    position: absolute;
  }

  &--flat &__spinner {
    border: 2px solid rgb(0 0 0 / 20%);
    border-top-color: var(--ui-button-color);
  }

  &--outline &__spinner {
    border: 2px solid rgb(0 0 0 / 20%);
    border-top-color: var(--ui-button-color);
  }

  &--loading &__content {
    visibility: hidden;
  }

  &--loading {
    pointer-events: none;
  }
  &--color-primary {
    --ui-button-color: #{$primary};
  }

  &--color-secondary {
    --ui-button-color: #{$secondary};
  }

  &--color-success {
    --ui-button-color: #{$success};
  }

  &--color-warning {
    --ui-button-color: #{$warning};
  }

  &--color-accent {
    --ui-button-color: #{$accent};
  }

  &--color-neutral {
    --ui-button-color: #{$neutral};
    --ui-button-text-color: #{$primary};
    --ui-button-plain-color: #{$primary};
  }
  &--color-white {
    --ui-button-color: #{$white};
    --ui-button-text-color: #{$tree};
  }
}

@keyframes ui-button-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
